<template>
  <PrintPageLandscape>
    <PresentationPage :page-number="pageNumber" :page-count="pageCount">
      <p class="presentation-page__eyebrow">IAB · SAB · AfA</p>
      <h2 class="presentation-page__title">Steuerbetrachtung — drei Hebel</h2>
      <p class="presentation-page__text">
        Gesamtinvestition {{ totalInvestment }} · Investor-Profil {{ investorProfile }} · zvE
        {{ taxableIncome }} · Steuersatz {{ taxRatePercent }}.
      </p>
      <div class="presentation-page__tax-cards">
        <article
          v-for="card in taxCards"
          :key="card.step"
          :class="['presentation-page__tax-card', card.cardClass]"
        >
          <div :class="['presentation-page__tax-card-step', card.stepClass]">{{ card.step }}</div>
          <h3 :class="['presentation-page__tax-card-title', card.titleClass]">{{ card.title }}</h3>
          <p class="presentation-page__tax-card-subtitle">{{ card.subtitle }}</p>
          <div class="presentation-page__tax-card-divider" aria-hidden="true"></div>
          <div class="presentation-page__tax-card-metric">
            <p class="presentation-page__tax-card-metric-label">Betrag</p>
            <p :class="['presentation-page__tax-card-metric-value', card.amountClass]">
              {{ card.amount }}
            </p>
          </div>
          <div class="presentation-page__tax-card-metric">
            <p class="presentation-page__tax-card-metric-label">
              Steuererstattung ({{ taxRatePercent }})
            </p>
            <p
              class="presentation-page__tax-card-metric-value presentation-page__tax-card-metric-value--brand-teal"
            >
              {{ card.refund }}
            </p>
          </div>
        </article>
      </div>
      <div class="presentation-page__tax-summary">
        <div>
          <p class="presentation-page__tax-summary-eyebrow">Mögliche Gesamt-Steuererstattung</p>
          <p class="presentation-page__tax-summary-text">{{ totalTaxRefundPercentOfInvestment }}</p>
        </div>
        <p class="presentation-page__tax-summary-value">{{ totalTaxRefund }}</p>
      </div>
      <p class="presentation-page__disclaimer">
        Musterberechnung ohne Haftung auf Richtigkeit. Keine Steuer- oder Rechtsberatung.
      </p>
    </PresentationPage>
  </PrintPageLandscape>
</template>

<script>
import PrintPageLandscape from '@/components/Print/PrintPageLandscape.vue';
import PresentationPage from '@/views/MasterPresentation/PresentationPage';
import { formatNumber, NUMBER_FORMAT, parseFiniteNumber } from '@/helpers/numberFormat';

const taxCardDefinitions = [
  {
    step: '1',
    cardClass: null,
    stepClass: null,
    titleClass: null,
    amountClass: null,
    title: 'Investitionsabzugsbetrag (IAB)',
    subtitle: 'im Jahr 2024 · bis zu 50 % des Kaufpreises',
    amountKey: 'iabAmount',
    refundKey: 'iabTaxRefund',
  },
  {
    step: '2',
    cardClass: 'presentation-page__tax-card--brand-secondary',
    stepClass: 'presentation-page__tax-card-step--brand-secondary',
    titleClass: 'presentation-page__tax-card-title--brand-secondary',
    amountClass: 'presentation-page__tax-card-metric-value--brand-secondary',
    title: 'Sonderabzug (SAB)',
    subtitle: 'im Jahr 2025 · 40 % des Restwertes nach IAB',
    amountKey: 'sabAmount',
    refundKey: 'sabTaxRefund',
  },
  {
    step: '3',
    cardClass: 'presentation-page__tax-card--brand-teal',
    stepClass: 'presentation-page__tax-card-step--brand-teal',
    titleClass: 'presentation-page__tax-card-title--brand-teal',
    amountClass: null,
    title: 'Lineare Abschreibung (AfA)',
    subtitle: 'im Jahr 2025 · 5 % des Restwertes',
    amountKey: 'afaAmount',
    refundKey: 'afaTaxRefund',
  },
];

function formatTaxRatePercent(rawTaxRate) {
  const taxRate = parseFiniteNumber(rawTaxRate);
  if (taxRate === null) {
    return formatNumber(null, NUMBER_FORMAT.DECIMAL);
  }

  const taxRatePercent = taxRate <= 1 ? taxRate * 100 : taxRate;
  return `${formatNumber(taxRatePercent, NUMBER_FORMAT.DECIMAL)} %`;
}

export default {
  name: 'MasterPresentationPrintPage40',
  components: { PrintPageLandscape, PresentationPage },
  props: {
    project: { type: Object, default: null },
    pageCount: { type: Number, default: null },
    pageNumber: { type: Number, default: 0 },
  },
  computed: {
    taxConsideration() {
      return this.project?.finModel?.taxConsideration ?? {};
    },

    totalInvestment() {
      return formatNumber(this.taxConsideration.totalInvestment, NUMBER_FORMAT.CURRENCY);
    },

    investorProfile() {
      return this.taxConsideration.investorProfile || '–';
    },

    taxableIncome() {
      return formatNumber(this.taxConsideration.taxableIncome, NUMBER_FORMAT.CURRENCY);
    },

    taxRatePercent() {
      return formatTaxRatePercent(this.taxConsideration.taxRate);
    },

    totalTaxRefund() {
      return formatNumber(this.taxConsideration.totalTaxRefund, NUMBER_FORMAT.CURRENCY);
    },

    totalTaxRefundPercentOfInvestment() {
      const totalInvestment = parseFiniteNumber(this.taxConsideration.totalInvestment);
      const totalTaxRefund = parseFiniteNumber(this.taxConsideration.totalTaxRefund);
      if (totalInvestment === null || totalTaxRefund === null || totalInvestment === 0) {
        return formatNumber(null, NUMBER_FORMAT.DECIMAL);
      }

      const refundPercent = (totalTaxRefund / totalInvestment) * 100;
      return `≈ ${formatNumber(refundPercent, NUMBER_FORMAT.DECIMAL)} % der Gesamtinvestition`;
    },

    taxCards() {
      return taxCardDefinitions.map((definition) => ({
        step: definition.step,
        cardClass: definition.cardClass,
        stepClass: definition.stepClass,
        titleClass: definition.titleClass,
        amountClass: definition.amountClass,
        title: definition.title,
        subtitle: definition.subtitle,
        amount: formatNumber(this.taxConsideration[definition.amountKey], NUMBER_FORMAT.CURRENCY),
        refund: formatNumber(this.taxConsideration[definition.refundKey], NUMBER_FORMAT.CURRENCY),
      }));
    },
  },
};
</script>
